// frontend/src/App.jsx
// Test deployment update - 2025-09-20
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CookieConsent, { Cookies } from "react-cookie-consent";
import RouteChangeTracker from './components/RouteChangeTracker';
import Header from "./components/layout/Header";
import fetchData from './api/fetchData';
import Footer from "./components/layout/Footer";
import AdminLoginForm from './components/admin/auth/AdminLoginForm';
import AdminPanel from './components/admin/AdminPanel';
import AdminPortfolioEditForm from './components/admin/portfolio/AdminPortfolioEditForm';
import AdminMenuEditForm from './components/admin/menu/AdminMenuEditForm';
import AdminServiceEditForm from './components/admin/services/AdminServiceEditForm';
import AdminReviewEditForm from './components/admin/reviews/AdminReviewEditForm';
import ChatWidget from './components/chat/ChatWidget';
import LoadingScreen from './components/LoadingScreen';
import ReviewForm from './components/sections/ReviewForm';
import './styles.css';
import { initImageOptimizations } from './utils/imageOptimization';
import { initPerformanceOptimizations } from './utils/performance';


import HomePage from './pages/HomePage';
import PrivacyPolicy from './components/PrivacyPolicy';


const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

function ScrollToHashElement() {
  let location = useLocation();
  useEffect(() => {
    if (location.hash) {
      let element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);
  return null;
}

function App() {

  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isFormAnimating, setIsFormAnimating] = useState(false);
  const navigate = useNavigate();
  const [headerData, setHeaderData] = useState({});
  const [heroData, setHeroData] = useState({});
  const [aboutData, setAboutData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [portfolioData, setPortfolioData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [contactsData, setContactsData] = useState({});
  const [specialOfferData, setSpecialOfferData] = useState(null);
  const [isConsentGiven, setIsConsentGiven] = useState(false);

  // Оптимизированная загрузка фоновой картинки
  useEffect(() => {
    // Предзагружаем изображение для лучшей производительности
    const img = new Image();
    img.onload = () => {
      document.body.style.backgroundImage = "url('/background.svg')";
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundPosition = "center";
      document.body.style.backgroundRepeat = "no-repeat";
      document.body.style.backgroundAttachment = "fixed";
      // Плавное появление фона
      document.body.style.opacity = "1";
    };
    img.src = "/background.svg";

    // Инициализируем оптимизации изображений
    initImageOptimizations();

    // Инициализируем оптимизации производительности
    initPerformanceOptimizations();

    // Показываем контент после загрузки
    const timer = setTimeout(() => {
      document.body.classList.remove('loading');
      document.body.classList.add('loaded');
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const getData = async (langToFetch) => {
    console.log("🔄 getData called with language:", langToFetch);
    try {
      const mainContentResponse = await fetchData(`/public/get_site_data.php?lang=${langToFetch}`, "GET");
      console.log("📡 API Response:", mainContentResponse);
      if (mainContentResponse && mainContentResponse.status === 'success') {
        console.log("✅ Data loaded successfully");
        setHeaderData(mainContentResponse.header || {});
        setHeroData(mainContentResponse.hero || {});
        setAboutData(mainContentResponse.about || []);
        setServicesData(mainContentResponse.services || []);
        setPortfolioData(mainContentResponse.portfolio || []);
        setReviewsData(mainContentResponse.reviews || []);
        setContactsData(mainContentResponse.contacts || {});
      } else {
        console.error("❌ Failed to load main data:", mainContentResponse?.message || "Неизвестная ошибка загрузки основных данных.");
      }
    } catch (error) {
      console.error("❌ Ошибка при загрузке данных:", error.message);
    }

    // Загружаем данные Special Offer отдельно через Laravel API
    try {
      const { getSpecialOfferApiUrl } = await import('./config/api');
      const laravelApiUrl = getSpecialOfferApiUrl('special-offer');
      console.log("🎯 App.jsx: Loading Special Offer data from:", laravelApiUrl);
      const response = await fetch(`${laravelApiUrl}?t=${Date.now()}`);
      const specialOfferResponse = await response.json();
      console.log("🎯 App.jsx: Special Offer API Response:", specialOfferResponse);
      if (specialOfferResponse && specialOfferResponse.status === 'success') {
        console.log("🎯 App.jsx: Setting specialOfferData with coupons_left:", specialOfferResponse.offer?.coupons_left);
        setSpecialOfferData(specialOfferResponse.offer || null);
      } else {
        console.error("❌ Failed to load special offer data:", specialOfferResponse?.message);
      }
    } catch (error) {
      console.error("❌ Ошибка при загрузке Special Offer данных:", error.message);
    }
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
    navigate('/admin/panel');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    setIsAdminLoggedIn(false);
    console.log("App: handleAdminLogout - isAdminLoggedIn set to false, localStorage cleared.");
    navigate('/');
  };


  const handleOpenReviewForm = () => {
    // Скроллим к секции ревью
    const reviewsSection = document.getElementById('reviews');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Небольшая задержка для завершения скролла
    setTimeout(() => {
      setIsFormAnimating(true);
      setShowReviewForm(true);
      // Блокируем скролл body
      document.body.style.overflow = 'hidden';

      // Добавляем обработчик клавиши Escape
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          handleCloseReviewForm();
        }
      };

      document.addEventListener('keydown', handleEscape);

      // Сохраняем ссылку на обработчик для удаления
      window.reviewFormEscapeHandler = handleEscape;
    }, 500);
  };

  const handleCloseReviewForm = () => {
    setIsFormAnimating(false);
    // Разблокируем скролл body
    document.body.style.overflow = 'auto';

    // Удаляем обработчик клавиши Escape
    if (window.reviewFormEscapeHandler) {
      document.removeEventListener('keydown', window.reviewFormEscapeHandler);
      window.reviewFormEscapeHandler = null;
    }

    setTimeout(() => {
      setShowReviewForm(false);
    }, 300);
  };


  const handleContentUpdate = () => {
    getData(i18n.language);
  };


  useEffect(() => {
    // Всегда показываем cookie баннер для тестирования
    console.log('🍪 Cookie banner always visible for testing');
    setIsConsentGiven(false);
    document.body.classList.add('cookie-banner-visible');
    console.log('🍪 Added cookie-banner-visible class on initial load');
  }, []);

  // Отключаем автоматическое скрытие баннера для тестирования
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const consent = Cookies.get("myPortfolioCookieConsent") === "true";
  //     if (consent !== isConsentGiven) {
  //       console.log('🍪 Consent changed:', consent);
  //       setIsConsentGiven(consent);
  //     }
  //   }, 2000);

  //   return () => clearInterval(interval);
  // }, [isConsentGiven]);

  // Отключаем функцию принятия cookie для тестирования
  const handleAcceptCookie = () => {
    console.log('🍪 Cookie accept button clicked, but banner stays visible for testing');
    // setIsConsentGiven(true);
    // Принудительно обновляем состояние
    // setTimeout(() => {
    //   setIsConsentGiven(true);
    // }, 100);
  };

  // Отключаем управление классом body для тестирования
  // useEffect(() => {
  //   console.log('🍪 Cookie consent state changed:', isConsentGiven);
  //   if (!isConsentGiven) {
  //     document.body.classList.add('cookie-banner-visible');
  //     console.log('🍪 Added cookie-banner-visible class to body');
  //   } else {
  //     document.body.classList.remove('cookie-banner-visible');
  //     console.log('🍪 Removed cookie-banner-visible class from body');
  //   }
  // }, [isConsentGiven]);


  useEffect(() => {
    const loggedIn = localStorage.getItem('isAdminLoggedIn') === 'true';
    setIsAdminLoggedIn(loggedIn);
  }, []);


  // Загружаем данные при инициализации и при смене языка
  useEffect(() => {
    console.log("🔄 useEffect [i18n.language] triggered, language:", i18n.language);
    if (i18n.isInitialized) {
      console.log("✅ i18n is initialized, calling getData");
      getData(i18n.language);
    } else {
      console.log("❌ i18n is NOT initialized yet");
    }
  }, [i18n.language, i18n.isInitialized]);


  return (
    <div>
      <Header headerData={headerData} isAdminLoggedIn={isAdminLoggedIn} onLogout={handleAdminLogout} />
      <LoadingScreen>
        <div>
          {isConsentGiven && <RouteChangeTracker />}
          <ScrollToHashElement />
          <main>
            <Routes>
              <Route path="/" element={
                <HomePage
                  heroData={heroData}
                  aboutData={aboutData}
                  servicesData={servicesData}
                  portfolioData={portfolioData}
                  reviewsData={reviewsData}
                  specialOfferData={specialOfferData}
                  onAddReviewClick={handleOpenReviewForm}
                  showReviewForm={showReviewForm}
                  isFormAnimating={isFormAnimating}
                  handleCloseReviewForm={handleCloseReviewForm}
                  handleContentUpdate={handleContentUpdate}
                />
              } />

              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              <Route path="/admin/login" element={
                isAdminLoggedIn ? <Navigate to="/admin/panel" replace /> : <AdminLoginForm onLogin={handleAdminLogin} />
              } />

              <Route
                path="/admin/panel"
                element={isAdminLoggedIn ? <AdminPanel onContentUpdated={handleContentUpdate} onLogout={handleAdminLogout} /> : <Navigate to="/admin/login" />}
              />

              <Route
                path="/admin/menu/add"
                element={isAdminLoggedIn ? <AdminMenuEditForm onSaveSuccess={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />
              <Route
                path="/admin/menu/edit/:menuItemId"
                element={isAdminLoggedIn ? <AdminMenuEditForm onSaveSuccess={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />

              <Route
                path="/admin/portfolio/add"
                element={isAdminLoggedIn ? <AdminPortfolioEditForm onSaveSuccess={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />
              <Route
                path="/admin/portfolio/edit/:itemId"
                element={isAdminLoggedIn ? <AdminPortfolioEditForm onSaveSuccess={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />

              <Route
                path="/admin/services/add"
                element={isAdminLoggedIn ? <AdminServiceEditForm onContentUpdated={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />
              <Route
                path="/admin/services/edit/:serviceId"
                element={isAdminLoggedIn ? <AdminServiceEditForm onContentUpdated={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />
              <Route
                path="/admin/reviews/edit/:reviewId"
                element={isAdminLoggedIn ? <AdminReviewEditForm onContentUpdated={handleContentUpdate} /> : <Navigate to="/admin/login" />}
              />
            </Routes>
          </main>
          <Footer contactsData={contactsData} />
        </div>
      </LoadingScreen>

      <ChatWidget showCookieBanner={!isConsentGiven} />

      {showReviewForm && (
        <div
          className={`review-form-overlay ${isFormAnimating ? 'show' : ''}`}
          onClick={(e) => {
            // Закрываем попап при клике на фон
            if (e.target === e.currentTarget) {
              handleCloseReviewForm();
            }
          }}
        >
          <ReviewForm
            onClose={handleCloseReviewForm}
            onReviewAdded={handleContentUpdate}
          />
        </div>
      )}

      <CookieConsent
        location="bottom"
        buttonText={t('cookieConsent.buttonText')}
        cookieName="myPortfolioCookieConsent"
        cookieSecurity={false}
        disableStyles={true}
        className="cookie-consent-banner"
        buttonStyle={{
          color: "white",
          fontSize: "14px",
          borderRadius: "6px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          padding: "8px 16px",
          fontWeight: "600",
          cursor: "pointer",
          transition: "all 0.3s ease",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
        }}
        expires={150}
        onAccept={handleAcceptCookie}
      >
        {t('cookieConsent.text')}{" "}
        <Link to="/privacy-policy" style={{ color: "yellow" }}>{t('cookieConsent.learnMore')}</Link>
      </CookieConsent>
    </div>
  );
}

export default App;